import { LogoLight } from '@/components/logo-light.component';
import { Logo } from '@/components/logo.component';
import { useSignupsPaused } from '@/hooks/use-signups-paused.hook';
import { useTheme } from '@/hooks/use-theme.hook';
import { Link, useRouter } from '@tanstack/react-router';
import { ReactNode, useEffect } from 'react';

interface AuthLayoutProps {
  leftPanel: ReactNode;
  rightPanel: ReactNode;
}

export const AuthLayout = ({ leftPanel, rightPanel }: AuthLayoutProps) => {
  const router = useRouter();
  const isLoginPage = router.state.location.pathname === '/auth/login';
  const { theme } = useTheme();
  const signupsPaused = useSignupsPaused();

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <div className='flex min-h-screen flex-col bg-linear-to-br from-slate-50 via-white to-slate-50 xl:flex-row dark:from-slate-950 dark:via-slate-900 dark:to-slate-950'>
      {/* Left Panel - Auth Form.
          The Clerk <SignUp/> card is a fixed 450px wide and cannot shrink, so
          the two-column split must wait until the left column is wider than
          that or the form spills into the right panel. At `lg` (1024px) the
          40% column is only ~410px — too narrow — so we hold single-column
          (tablet view: form full-width, right panel hidden) until `xl`
          (1280px), where the 45% column gives ~480px of room. */}
      <div className='flex h-screen w-full min-w-0 flex-col overflow-x-hidden overflow-y-auto px-8 py-6 xl:w-[45%] xl:px-12'>
        <div className='mb-6 flex shrink-0 items-center justify-between'>
          {theme === 'dark' ? (
            <LogoLight className='h-10 w-auto' />
          ) : (
            <Logo className='h-10 w-auto' />
          )}
        </div>
        <div className='flex flex-1 items-center justify-center'>
          <div className='w-full max-w-md'>{leftPanel}</div>
        </div>
      </div>

      {/* Right Panel - Preview Content */}
      <div className='hidden h-screen w-full flex-col overflow-y-auto border-l border-white/10 bg-[#0e1015] bg-[radial-gradient(ellipse_at_top,rgba(62,139,255,0.14),transparent_60%)] p-8 xl:flex xl:w-[55%]'>
        <div className='flex shrink-0 items-center justify-end'>
          {!(isLoginPage && signupsPaused) && (
            <Link
              to={isLoginPage ? '/auth/register' : '/auth/login'}
              className='text-sm text-white/60 hover:text-white'
            >
              {isLoginPage ? (
                <>
                  Don't have an account?{' '}
                  <span className='font-semibold text-[#8fb6ff] hover:text-white'>
                    Sign up
                  </span>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <span className='font-semibold text-[#8fb6ff] hover:text-white'>
                    Sign in
                  </span>
                </>
              )}
            </Link>
          )}
        </div>
        <div className='flex flex-1 items-center justify-center'>
          <div className='w-full max-w-xl'>{rightPanel}</div>
        </div>
      </div>
    </div>
  );
};
